import { useEffect, useState } from 'react';
import {
  CheckCircle, ChevronDown, Clock, ExternalLink, MessageCircle, Search, Wrench, XCircle,
} from 'lucide-react';
import { AdminActionBanner } from '@/components/admin/AdminActionBanner';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { CrmMeasurementReportBlock } from '@/components/admin/crm/CrmMeasurementReportBlock';
import { AdminMeasurementDetailsEditor } from '@/components/admin/AdminMeasurementDetailsEditor';
import { openTelegramUserDm } from '@/openTelegramUser';
import {
  ADMIN_MEAS_ACTION_BTN,
  ADMIN_MEAS_ACTION_GRID,
  type MeasurementInterest,
  type MeasurementSourceFilter,
  extractMeasurementSourceLabel,
  MEASUREMENT_STATUS_META,
} from './adminOrdersShared';

export type AdminMeasurement = {
  id: number;
  status: string;
  city?: string;
  phone?: string;
  preferred_time?: string;
  notes?: string;
  created_at?: string | null;
  interest?: string | null;
  assigned_measurer_id?: number | null;
  order_id?: number | null;
  report_notes?: string | null;
  completed_at?: string | null;
  attachment_count?: number;
  user: { id: number; telegram_id: number; first_name?: string; username?: string };
};

export type AdminMeasurementsPanelProps = {
  canManageStaff: boolean;
  fullAdmin: boolean;
  showMeasurementsPanel: boolean;
  setShowMeasurementsPanel: (open: boolean) => void;
  pendingMeasurementCount: number;
  retentionRunning: boolean;
  retentionResult: {
    ok: boolean;
    dry_run?: boolean;
    sent_count?: number;
    sent: Array<{ measurement_id: number; telegram_id: number; tag: string }>;
  } | null;
  runMeasurementRetention: (dryRun: boolean) => void;
  measurementInterest: MeasurementInterest;
  setMeasurementInterest: (v: MeasurementInterest) => void;
  measurementSourceFilter: MeasurementSourceFilter;
  setMeasurementSourceFilter: (v: MeasurementSourceFilter) => void;
  measurementsBySource: Record<string, number>;
  measurementSearch: string;
  setMeasurementSearch: (v: string) => void;
  measurementSort: 'newest' | 'city';
  setMeasurementSort: (v: 'newest' | 'city') => void;
  measurementsLoading: boolean;
  measurements: AdminMeasurement[];
  filteredMeasurements: AdminMeasurement[];
  measurers: Array<{ id: number; first_name?: string; username?: string }>;
  assigningMeasurer: number | null;
  highlightMeasurementId: number | null;
  filterUserId?: number | null;
  onClearFilterUserId?: () => void;
  updating: number | null;
  haptic: { selection: () => void; impact: (s: 'light' | 'medium' | 'heavy') => void };
  updateMeasurementStatus: (id: number, status: 'scheduled' | 'completed' | 'cancelled') => void;
  assignMeasurerToRequest: (measurementId: number, measurerUserId: number) => void;
  assignMeasurerMessage?: { measurementId: number; text: string; tone: 'success' | 'error' } | null;
  onOpenOrderDeal?: (orderId: number) => void;
  onRefreshMeasurements?: () => void;
  viewerRole?: string | null;
  viewerUserId?: number | null;
};

export function AdminMeasurementsPanel(props: AdminMeasurementsPanelProps) {
  const {
    canManageStaff,
    fullAdmin,
    showMeasurementsPanel,
    setShowMeasurementsPanel,
    pendingMeasurementCount,
    retentionRunning,
    retentionResult,
    runMeasurementRetention,
    measurementInterest,
    setMeasurementInterest,
    measurementSourceFilter,
    setMeasurementSourceFilter,
    measurementsBySource,
    measurementSearch,
    setMeasurementSearch,
    measurementSort,
    setMeasurementSort,
    measurementsLoading,
    measurements,
    filteredMeasurements,
    measurers,
    assigningMeasurer,
    highlightMeasurementId,
    filterUserId,
    onClearFilterUserId,
    updating,
    haptic,
    updateMeasurementStatus,
    assignMeasurerToRequest,
    assignMeasurerMessage,
    onOpenOrderDeal,
    onRefreshMeasurements,
    viewerRole,
    viewerUserId,
  } = props;

  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (highlightMeasurementId == null) return;
    setExpandedIds((prev) => {
      if (prev.has(highlightMeasurementId)) return prev;
      const next = new Set(prev);
      next.add(highlightMeasurementId);
      return next;
    });
  }, [highlightMeasurementId]);

  const toggleExpanded = (id: number) => {
    haptic.selection();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!canManageStaff) return null;

  return (
    <>
      {canManageStaff && (
            <AdminGlassDisclosure
              className="mb-2"
              accent="emerald"
              open={showMeasurementsPanel}
              onOpenChange={setShowMeasurementsPanel}
              title="Замеры"
              subtitle="Назначение замерщика, статусы, связь с клиентом"
              icon={<Wrench size={16} color="#68D391" />}
              badge={pendingMeasurementCount > 0 ? pendingMeasurementCount : measurements.length}
            >
              {fullAdmin && (
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  disabled={retentionRunning}
                  onClick={() => { haptic.selection(); runMeasurementRetention(true); }}
                  className="press px-2.5 py-1 rounded-lg text-[10px] font-bold disabled:opacity-50"
                  style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                >
                  {retentionRunning ? '...' : 'Проверка retention'}
                </button>
                <button
                  type="button"
                  disabled={retentionRunning}
                  onClick={() => { haptic.impact('light'); runMeasurementRetention(false); }}
                  className="press px-2.5 py-1 rounded-lg text-[10px] font-bold disabled:opacity-50"
                  style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}
                >
                  {retentionRunning ? '...' : 'Запустить retention'}
                </button>
              </div>
              )}
              {fullAdmin && retentionResult && (
                <div
                  className="rounded-lg px-2.5 py-2 mb-2 text-[10px]"
                  style={{
                    background: retentionResult.ok ? 'rgba(72,187,120,0.12)' : 'rgba(252,129,74,0.12)',
                    color: retentionResult.ok ? '#68D391' : '#FC814A',
                    border: `1px solid ${retentionResult.ok ? 'rgba(72,187,120,0.35)' : 'rgba(252,129,74,0.35)'}`,
                  }}
                >
                  {retentionResult.ok
                    ? `${retentionResult.dry_run ? 'Dry-run' : 'Run'}: ${retentionResult.sent_count} кандидатов/отправок`
                    : 'Не удалось запустить retention'}
                  {retentionResult.sent.length > 0 && (
                    <div className="mt-1 space-y-0.5" style={{ color: 'var(--text-2)' }}>
                      {retentionResult.sent.slice(0, 4).map((row) => (
                        <p key={`${row.measurement_id}-${row.telegram_id}-${row.tag}`}>
                          #{row.measurement_id} · TG {row.telegram_id} · {row.tag.toUpperCase()}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar mb-2">
                {([
                  ['', 'Все'],
                  ['sliding', 'Карнизы'],
                  ['roman', 'Римские'],
                  ['roller', 'Рулонные'],
                  ['smarthome', 'Smart home'],
                ] as const).map(([key, label]) => (
                  <button
                    key={key || 'all'}
                    onClick={() => setMeasurementInterest(key as MeasurementInterest)}
                    className="press shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                    style={measurementInterest === key
                      ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                      : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar mb-2">
                {([
                  ['', 'Все источники'],
                  ['Каталог', `Каталог (${measurementsBySource['Каталог']})`],
                  ['Карточка решения', `Карточка решения (${measurementsBySource['Карточка решения']})`],
                  ['Карточка товара', `Карточка товара (${measurementsBySource['Карточка товара']})`],
                ] as const).map(([key, label]) => (
                  <button
                    key={key || 'all-sources'}
                    onClick={() => setMeasurementSourceFilter(key as MeasurementSourceFilter)}
                    className="press shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                    style={measurementSourceFilter === key
                      ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                      : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {filterUserId != null && (
                <div
                  className="rounded-xl px-3 py-2 mb-2 flex items-center justify-between gap-2"
                  style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid var(--cyan-border)' }}
                >
                  <p className="text-[10px] font-semibold" style={{ color: 'var(--cyan-bright)' }}>
                    Показаны замеры клиента #{filterUserId}
                  </p>
                  {onClearFilterUserId && (
                    <button
                      type="button"
                      onClick={() => { haptic.selection(); onClearFilterUserId(); }}
                      className="press shrink-0 px-2 py-1 rounded-lg text-[10px] font-bold"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-glass)' }}
                    >
                      Все клиенты
                    </button>
                  )}
                </div>
              )}
              <div className="relative mb-2">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-3)' }} />
                <input
                  type="text"
                  value={measurementSearch}
                  onChange={(e) => setMeasurementSearch(e.target.value)}
                  placeholder="Поиск: город, клиент, телефон..."
                  className="input-glass w-full pl-8 pr-2 py-1.5 rounded-lg text-[11px]"
                />
              </div>
              <div className="flex gap-1.5 mb-2">
                <button
                  onClick={() => setMeasurementSort('newest')}
                  className="press px-2 py-1 rounded-full text-[10px] font-semibold"
                  style={measurementSort === 'newest'
                    ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                    : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                >
                  Сначала новые
                </button>
                <button
                  onClick={() => setMeasurementSort('city')}
                  className="press px-2 py-1 rounded-full text-[10px] font-semibold"
                  style={measurementSort === 'city'
                    ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                    : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                >
                  По городу А-Я
                </button>
              </div>
              {measurementsLoading ? (
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>Загружаем замеры...</p>
              ) : measurements.length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>Нет замеров для фильтра</p>
              ) : filteredMeasurements.length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>Нет замеров по текущему поиску/источнику</p>
              ) : (
                <div className="space-y-2.5 max-h-[min(70vh,560px)] overflow-y-auto pr-0.5">
                  {filteredMeasurements.map((m) => {
                      const sourceLabel = extractMeasurementSourceLabel(m.notes);
                      const statusMeta = MEASUREMENT_STATUS_META[m.status] || {
                        label: m.status,
                        color: 'var(--text-2)',
                        bg: 'rgba(255,255,255,0.08)',
                        border: 'rgba(255,255,255,0.18)',
                      };
                      const expanded = expandedIds.has(m.id);
                      const assignedMeasurer = measurers.find((z) => z.id === m.assigned_measurer_id);
                      const clientLabel = m.user.first_name || m.user.username || `TG ${m.user.telegram_id}`;
                      return (
                    <div
                      key={m.id}
                      className="rounded-2xl overflow-hidden"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: highlightMeasurementId === m.id
                          ? '1.5px solid var(--cyan)'
                          : '1px solid var(--border-glass)',
                        boxShadow: highlightMeasurementId === m.id ? '0 0 16px rgba(99,179,237,0.35)' : undefined,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleExpanded(m.id)}
                        className="press w-full text-left p-3 flex items-start gap-2 touch-manipulation min-h-[44px]"
                        aria-expanded={expanded}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span className="text-[11px] font-bold" style={{ color: 'var(--text-1)' }}>#{m.id}</span>
                            <span
                              className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ background: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.border}` }}
                            >
                              {statusMeta.label}
                            </span>
                            {sourceLabel && (
                              <span
                                className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: 'rgba(99,179,237,0.14)', color: 'var(--cyan)', border: '1px solid var(--cyan-border)' }}
                              >
                                {sourceLabel}
                              </span>
                            )}
                            {assignedMeasurer && !expanded && (
                              <span className="text-[10px] font-medium truncate max-w-[120px]" style={{ color: 'var(--text-3)' }}>
                                · {assignedMeasurer.first_name || assignedMeasurer.username}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>
                            {clientLabel}
                          </p>
                          <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--text-2)' }}>
                            {m.city || 'Город не указан'}
                            {m.phone ? ` · ${m.phone}` : ''}
                          </p>
                          {!expanded && (
                            <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--text-3)' }}>
                              {m.preferred_time || 'Время не указано'}
                              {m.created_at ? ` · ${new Date(m.created_at).toLocaleDateString('ru-RU')}` : ''}
                              {(m.attachment_count ?? 0) > 0 ? ` · 📷 ${m.attachment_count}` : ''}
                              {m.order_id ? ` · ONV #${m.order_id}` : ''}
                            </p>
                          )}
                        </div>
                        <ChevronDown
                          size={18}
                          className={`shrink-0 mt-0.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                          style={{ color: 'var(--text-3)' }}
                        />
                      </button>

                      {expanded && (
                      <div className="flex flex-col gap-2.5 px-3 pb-3 pt-0 border-t border-white/5">
                        <p className="text-[11px] truncate -mt-0.5" style={{ color: 'var(--text-3)' }}>
                          {m.preferred_time || 'Время не указано'}
                          {m.created_at ? ` · ${new Date(m.created_at).toLocaleDateString('ru-RU')}` : ''}
                        </p>
                        <div
                          className="mt-2 rounded-xl p-2.5"
                          style={{ background: 'rgba(159,122,234,0.06)', border: '1px solid rgba(159,122,234,0.22)' }}
                        >
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#B794F4' }}>
                            Адрес и комментарий
                          </p>
                          <AdminMeasurementDetailsEditor
                            measurementId={m.id}
                            initialCity={m.city ?? ''}
                            initialPreferredTime={m.preferred_time ?? ''}
                            initialNotes={m.notes ?? ''}
                            canEdit={canManageStaff}
                            hapticSelection={haptic.selection}
                            hapticSuccess={() => haptic.impact('medium')}
                            onSaved={() => onRefreshMeasurements?.()}
                          />
                        </div>
                        {(m.attachment_count ?? 0) > 0 || m.report_notes ? (
                          <p className="text-[11px] mt-1" style={{ color: 'var(--cyan)' }}>
                            {(m.attachment_count ?? 0) > 0 ? `📷 ${m.attachment_count} фото` : ''}
                            {(m.attachment_count ?? 0) > 0 && m.report_notes ? ' · ' : ''}
                            {m.report_notes ? 'есть отчёт' : ''}
                            {m.completed_at ? ` · ${new Date(m.completed_at).toLocaleDateString('ru-RU')}` : ''}
                          </p>
                        ) : m.status !== 'completed' ? (
                          <p className="text-[11px] mt-1" style={{ color: 'var(--text-3)' }}>
                            Фото и отчёт появятся после «Завершить замер» у замерщика
                          </p>
                        ) : null}
                        {m.order_id && onOpenOrderDeal ? (
                          <button
                            type="button"
                            onClick={() => {
                              haptic.selection();
                              onOpenOrderDeal(m.order_id!);
                            }}
                            className="mt-2 text-[11px] font-semibold inline-flex items-center gap-1"
                            style={{ color: 'var(--cyan-bright)' }}
                          >
                            <ExternalLink size={12} /> Карточка сделки · заказ #{m.order_id}
                          </button>
                        ) : null}

                      {measurers.length > 0 ? (
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'var(--cyan)' }}>
                            Замерщик на выезд
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {measurers.map((z) => {
                              const selected = m.assigned_measurer_id === z.id;
                              const label = z.first_name || z.username || `Замерщик #${z.id}`;
                              return (
                                <button
                                  key={z.id}
                                  type="button"
                                  disabled={assigningMeasurer === m.id}
                                  onClick={() => {
                                    if (selected) return;
                                    haptic.selection();
                                    void assignMeasurerToRequest(m.id, z.id);
                                  }}
                                  className="press px-3 py-2 rounded-xl text-[12px] font-semibold min-h-[44px] touch-manipulation disabled:opacity-50"
                                  style={selected
                                    ? { background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1.5px solid rgba(72,187,120,0.5)' }
                                    : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                                  aria-pressed={selected}
                                >
                                  {assigningMeasurer === m.id && !selected ? '…' : label}
                                </button>
                              );
                            })}
                          </div>
                          {assignMeasurerMessage && assignMeasurerMessage.measurementId === m.id && (
                            <AdminActionBanner
                              className="mt-2"
                              text={assignMeasurerMessage.text}
                              tone={assignMeasurerMessage.tone}
                            />
                          )}
                        </div>
                      ) : canManageStaff ? (
                        <p className="text-[11px] px-2 py-2 rounded-xl" style={{ color: 'var(--text-3)', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}>
                          Нет замерщиков. Во вкладке «Клиенты» назначьте роль «Замерщик».
                        </p>
                      ) : null}

                      {(m.assigned_measurer_id || m.status === 'scheduled' || m.status === 'completed') ? (
                        <div
                          className="rounded-xl p-2.5"
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
                        >
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>
                            Отчёт с выезда
                          </p>
                          <CrmMeasurementReportBlock
                            measurement={{
                              id: m.id,
                              status: m.status,
                              report_notes: m.report_notes ?? null,
                              completed_at: m.completed_at ?? null,
                              attachment_count: m.attachment_count ?? 0,
                            }}
                            orderId={m.order_id ?? undefined}
                            canEdit={viewerRole === 'measurer' && viewerUserId === m.assigned_measurer_id}
                            hapticSelection={haptic.selection}
                            hapticSuccess={() => haptic.impact('medium')}
                            onUpdated={() => onRefreshMeasurements?.()}
                          />
                        </div>
                      ) : null}

                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-3)' }}>
                          Действия
                        </p>
                        <div className={ADMIN_MEAS_ACTION_GRID}>
                          <button
                            type="button"
                            onClick={() => {
                              haptic.selection();
                              openTelegramUserDm(m.user.telegram_id, m.user.username);
                            }}
                            className={ADMIN_MEAS_ACTION_BTN}
                            style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                          >
                            <MessageCircle size={14} /> Написать
                          </button>
                          <button
                            type="button"
                            onClick={() => void updateMeasurementStatus(m.id, 'scheduled')}
                            disabled={updating === m.id || m.status === 'scheduled' || m.status === 'completed'}
                            className={ADMIN_MEAS_ACTION_BTN}
                            style={{ background: 'rgba(246,173,85,0.15)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.4)' }}
                          >
                            <Clock size={14} /> В работу
                          </button>
                          <button
                            type="button"
                            title="Без фото/отчёта лучше дождаться замерщика — иначе менеджер не увидит результат"
                            onClick={() => {
                              const hasReport = (m.attachment_count ?? 0) > 0 || Boolean(m.report_notes?.trim());
                              if (!hasReport && !window.confirm(
                                'Нет фото и текста отчёта. Завершить всё равно? Результаты замера останутся пустыми для команды.',
                              )) return;
                              void updateMeasurementStatus(m.id, 'completed');
                            }}
                            disabled={updating === m.id || m.status === 'completed'}
                            className={ADMIN_MEAS_ACTION_BTN}
                            style={{ background: 'rgba(72,187,120,0.15)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}
                          >
                            <CheckCircle size={14} /> Завершён
                          </button>
                          <button
                            type="button"
                            onClick={() => void updateMeasurementStatus(m.id, 'cancelled')}
                            disabled={updating === m.id || m.status === 'cancelled'}
                            className={ADMIN_MEAS_ACTION_BTN}
                            style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
                          >
                            <XCircle size={14} /> Отменён
                          </button>
                        </div>
                      </div>
                      </div>
                      )}
                    </div>
                  )})}
                </div>
              )}
            </AdminGlassDisclosure>
      )}
    </>
  );
}
